import {
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import { format } from "date-fns";
import { V2DetailInfo } from "../../commons";
import { SectionTitle, getMeasurementTermString } from "./commons";

export interface InsomniaRiskContent3Props {
  detailInfoArray: V2DetailInfo[];
  measurementPeriodEnd: string;
  measurementPeriodStart: string;
}

export default function InsomniaRiskContent3({
  detailInfoArray,
  measurementPeriodEnd,
  measurementPeriodStart,
}: InsomniaRiskContent3Props) {
  const measurementTermString = getMeasurementTermString(
    measurementPeriodStart,
    measurementPeriodEnd
  );
  return (
    <>
      <Stack sx={{ gap: "24px" }}>
        <Stack direction="row" sx={{ justifyContent: "space-between" }}>
          <SectionTitle text="参考情報：睡眠測定データの詳細" />
          <Typography sx={{ fontSize: "10px", fontWeight: 400, color: "#000" }}>
            測定期間：{measurementTermString}
          </Typography>
        </Stack>
        <MeasurementDetailTable detailInfoArray={detailInfoArray} />
      </Stack>
    </>
  );
}

interface MeasurementDetailTableProps {
  detailInfoArray: V2DetailInfo[];
}

function MeasurementDetailTable({
  detailInfoArray,
}: MeasurementDetailTableProps) {
  const dateArray = detailInfoArray.map((_, i) => `${i + 1}日目`);
  const datetimeStringToTimeString = (datetimeString: string | null) => {
    if (datetimeString == null) return "-";
    const date = new Date(datetimeString);
    return format(date, "H:mm");
  };

  return (
    <Stack>
      <TableContainer
        sx={{
          ml: "8px",
          width: "100%",
          border: "solid #000",
          borderWidth: "1px 1px 0 1px",
          borderRadius: 0,
        }}
      >
        <Table>
          <TableHead>
            <MeasurementDetailTableHeader dateArray={dateArray} />
          </TableHead>
          <TableBody>
            <MeasurementDetailTableRow
              itemName="消灯時刻"
              dataArray={detailInfoArray.map((d) =>
                datetimeStringToTimeString(d.bedtime)
              )}
            />
            <MeasurementDetailTableRow
              itemName="入眠時刻"
              dataArray={detailInfoArray.map((d) =>
                datetimeStringToTimeString(d.sleep_start_time)
              )}
            />
            <MeasurementDetailTableRow
              itemName="覚醒時刻"
              dataArray={detailInfoArray.map((d) =>
                datetimeStringToTimeString(d.sleep_end_time)
              )}
            />
            <MeasurementDetailTableRow
              itemName="起床時刻"
              dataArray={detailInfoArray.map((d) =>
                datetimeStringToTimeString(d.arise_time)
              )}
            />
            <MeasurementDetailTableRow
              itemName="起床希望時刻"
              itemSubName="(主観値)"
              dataArray={detailInfoArray.map((d) =>
                datetimeStringToTimeString(d.planned_wakeup_time)
              )}
            />
            <MeasurementDetailTableRow
              itemName="総就床時間"
              dataArray={detailInfoArray.map(
                (d) => d.sleep_summary.total_time_in_bed
              )}
            />
            <MeasurementDetailTableRow
              itemName="睡眠時間"
              dataArray={detailInfoArray.map((d) => d.sleep_summary.sleep_time)}
            />
            <MeasurementDetailTableRow
              itemName="総睡眠時間"
              dataArray={detailInfoArray.map(
                (d) => d.sleep_summary.total_sleep_time
              )}
            />
            <MeasurementDetailTableRow
              itemName="睡眠効率"
              dataArray={detailInfoArray.map(
                (d) => d.sleep_summary.sleep_efficiency
              )}
            />
            <MeasurementDetailTableRow
              itemName="入眠潜時"
              dataArray={detailInfoArray.map(
                (d) => d.sleep_summary.sleep_latency
              )}
            />
            <MeasurementDetailTableRow
              itemName="離床潜時"
              dataArray={detailInfoArray.map(
                (d) => d.sleep_summary.wakeup_latency
              )}
            />
            <MeasurementDetailTableRow
              itemName="中途覚醒時間"
              dataArray={detailInfoArray.map(
                (d) => d.sleep_summary.awakening_time
              )}
            />
            <MeasurementDetailTableRow
              itemName="覚醒回数"
              dataArray={detailInfoArray.map(
                (d) => d.sleep_summary.awakening_count
              )}
            />
            <MeasurementDetailTableRow
              itemName="1回あたり覚醒時間"
              dataArray={detailInfoArray.map(
                (d) => d.sleep_summary.awakening_average_time
              )}
            />
          </TableBody>
        </Table>
      </TableContainer>
    </Stack>
  );
}

interface MeasurementDetailTableHeaderProps {
  dateArray: string[];
}

function MeasurementDetailTableHeader({
  dateArray,
}: MeasurementDetailTableHeaderProps) {
  const length = dateArray.length;
  if (length === 0) return null;
  return (
    <TableRow
      sx={{
        height: "38px",
        "& .MuiTableCell-root": {
          p: 0,
          borderBottom: "1px solid #000",
          backgroundColor: "#EAEAEA80",
        },
        "& .MuiTypography-root": {
          color: "#000",
        },
      }}
    >
      <TableCell sx={{ width: "120px" }} />
      {dateArray.map((date, i) => (
        <TableCell key={i}>
          <Typography
            sx={{
              fontSize: "10px",
              fontWeight: 500,
              borderLeft: "1px solid #000",
              pl: "8px",
            }}
          >
            {date}
          </Typography>
        </TableCell>
      ))}
    </TableRow>
  );
}

interface MeasurementDetailTableRowProps {
  itemName: string;
  itemSubName?: string;
  dataArray: (string | null)[];
}

function MeasurementDetailTableRow({
  itemName,
  itemSubName,
  dataArray,
}: MeasurementDetailTableRowProps) {
  const length = dataArray.length;
  if (length === 0) return null;
  return (
    <TableRow sx={{ height: "36px", "& .MuiTableCell-root": { p: 0 } }}>
      <TableCell
        sx={{
          width: "120px",
          border: "solid #000",
          borderWidth: "0 1px 1px 0",
          backgroundColor: "#EAEAEA80",
          "& .MuiTypography-root": {
            color: "#000",
          },
        }}
      >
        {itemSubName ? (
          <Stack direction="row" sx={{ pl: "8px" }}>
            <Typography sx={{ fontSize: "10px", fontWeight: 500 }}>
              {itemName}
            </Typography>
            <Typography
              sx={{ fontSize: "8px", fontWeight: 500, alignSelf: "self-end" }}
            >
              {itemSubName}
            </Typography>
          </Stack>
        ) : (
          <Typography sx={{ fontSize: "10px", fontWeight: 500, pl: "8px" }}>
            {itemName}
          </Typography>
        )}
      </TableCell>

      {dataArray.map((date, i) => (
        <TableCell
          key={i}
          sx={{
            width: `calc((730px - 120px) / ${length})`,
            borderBottom: "1px solid #000",
            "& .MuiTypography-root": {
              color: "#000",
            },
          }}
        >
          <Typography sx={{ fontSize: "10px", fontWeight: 400, pl: "8px" }}>
            {date ?? "-"}
          </Typography>
        </TableCell>
      ))}
    </TableRow>
  );
}
